import { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle, XCircle, Users, Sparkles } from 'lucide-react';
import { useBulkLeadImport } from '@/hooks/useBulkLeadImport';
import { cn } from '@/lib/utils';

export function BulkLeadImport() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [skipInvalid, setSkipInvalid] = useState(false);
  const { 
    isValidating, 
    isImporting, 
    parsedLeads, 
    validationResult, 
    parseCSV, 
    validateLeads, 
    importLeads,
    reset 
  } = useBulkLeadImport();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const leads = parseCSV(content);
      if (leads.length > 0) {
        validateLeads(leads);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    importLeads({ skipDuplicates, skipInvalid });
  };

  const getQualityColor = (score: number) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-primary';
    if (score >= 40) return 'text-warning';
    return 'text-destructive';
  };

  const totalDuplicates = validationResult 
    ? validationResult.duplicatesInDatabase.length + (validationResult.batchSummary.duplicatesFound || 0)
    : 0;

  const leadsToImportCount = parsedLeads.length 
    - (skipDuplicates ? totalDuplicates : 0)
    - (skipInvalid && validationResult ? validationResult.batchSummary.totalInvalid : 0);

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-card to-card/80">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-primary" />
          Bulk Lead Import
        </CardTitle>
        <CardDescription>
          Import leads from CSV with AI-powered validation and duplicate detection
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* File Upload */}
        {!parsedLeads.length && (
          <div 
            className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
            <p className="text-sm font-medium mb-1">Click to upload CSV file</p>
            <p className="text-xs text-muted-foreground">
              Supported columns: first_name, last_name, email, phone, company_name, location, tags
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>
        )}

        {/* Validating State */}
        {isValidating && (
          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary animate-pulse" />
              <span className="text-sm font-medium">AI is validating your leads...</span>
            </div>
            <Progress value={45} className="animate-pulse" />
          </div>
        )}

        {/* Validation Results */}
        {validationResult && !isValidating && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <div className="text-xl font-bold">{parsedLeads.length}</div>
                <div className="text-xs text-muted-foreground">Total Leads</div>
              </div>
              <div className="p-3 rounded-lg bg-success/10 text-center">
                <CheckCircle className="h-5 w-5 mx-auto mb-1 text-success" />
                <div className="text-xl font-bold text-success">{validationResult.batchSummary.totalValid}</div>
                <div className="text-xs text-muted-foreground">Valid</div>
              </div>
              <div className="p-3 rounded-lg bg-destructive/10 text-center">
                <XCircle className="h-5 w-5 mx-auto mb-1 text-destructive" />
                <div className="text-xl font-bold text-destructive">{validationResult.batchSummary.totalInvalid}</div>
                <div className="text-xs text-muted-foreground">Invalid</div>
              </div>
              <div className="p-3 rounded-lg bg-warning/10 text-center">
                <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-warning" />
                <div className="text-xl font-bold text-warning">{totalDuplicates}</div>
                <div className="text-xs text-muted-foreground">Duplicates</div>
              </div>
            </div>

            {/* Quality Score */}
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Average Data Quality</span>
                <span className={cn("text-lg font-bold", getQualityColor(validationResult.batchSummary.averageQualityScore))}>
                  {Math.round(validationResult.batchSummary.averageQualityScore)}%
                </span>
              </div>
              <Progress 
                value={validationResult.batchSummary.averageQualityScore} 
                className="h-2"
              />
            </div>

            {/* Common Issues */}
            {validationResult.batchSummary.commonIssues?.length > 0 && (
              <div className="p-3 rounded-lg bg-warning/5 border border-warning/20">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  Common Issues Found
                </h4>
                <div className="flex flex-wrap gap-2">
                  {validationResult.batchSummary.commonIssues.map((issue, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {issue}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Lead Preview */}
            <div className="border rounded-lg">
              <div className="p-3 border-b bg-muted/30">
                <h4 className="text-sm font-medium">Lead Preview</h4>
              </div>
              <ScrollArea className="h-48">
                <div className="p-2 space-y-2">
                  {parsedLeads.slice(0, 20).map((lead, index) => {
                    const validation = validationResult.validatedLeads.find(v => v.index === index);
                    const isDuplicate = validationResult.duplicatesInDatabase.includes(index) ||
                      validation?.potentialDuplicateOf !== undefined;
                    
                    return (
                      <div 
                        key={index}
                        className={cn(
                          "flex items-center justify-between p-2 rounded text-sm",
                          validation?.isValid ? "bg-muted/30" : "bg-destructive/5",
                          isDuplicate && "bg-warning/5"
                        )}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {validation?.isValid ? (
                            <CheckCircle className="h-3 w-3 text-success shrink-0" />
                          ) : (
                            <XCircle className="h-3 w-3 text-destructive shrink-0" />
                          )}
                          <span className="truncate">
                            {lead.first_name} {lead.last_name}
                          </span>
                          <span className="text-muted-foreground truncate">
                            {lead.email}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isDuplicate && (
                            <Badge variant="outline" className="text-xs text-warning border-warning">
                              Duplicate
                            </Badge>
                          )}
                          <span className={cn("text-xs font-medium", getQualityColor(validation?.dataQualityScore || 0))}>
                            {validation?.dataQualityScore || 0}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {parsedLeads.length > 20 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      And {parsedLeads.length - 20} more leads...
                    </p>
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Import Options */}
            <div className="space-y-3 p-3 rounded-lg bg-muted/50">
              <h4 className="text-sm font-medium">Import Options</h4>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="skip-duplicates" 
                  checked={skipDuplicates}
                  onCheckedChange={(checked) => setSkipDuplicates(!!checked)}
                />
                <Label htmlFor="skip-duplicates" className="text-sm cursor-pointer">
                  Skip duplicates ({totalDuplicates} found)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="skip-invalid" 
                  checked={skipInvalid}
                  onCheckedChange={(checked) => setSkipInvalid(!!checked)}
                />
                <Label htmlFor="skip-invalid" className="text-sm cursor-pointer">
                  Skip invalid leads ({validationResult.batchSummary.totalInvalid} found)
                </Label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={reset} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={isImporting || leadsToImportCount <= 0}
                className="flex-1"
              >
                {isImporting ? 'Importing...' : `Import ${leadsToImportCount} Leads`}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
